/**
 * ADM Print Utility — bridges the RIV WebApp to ADMPrintApp via WLS protocol.
 *
 * WLS (Wavelink Scripting) is a custom URL scheme in Velocity Browser that dispatches
 * Android Intents to native apps. This utility handles:
 * - WLS protocol invocation (wls:printViaIP)
 * - Callback registration on window
 * - 30s timeout with cleanup
 * - AppIntentResponse parsing
 * - 1:1 contract enforcement (one job at a time)
 * - AWS RUM metric emission
 */

import { AppIntentResponse, ADM_PRINT_CONFIG } from 'src/models/AdmPrint';
import { awsRum } from 'src/serviceClients/aws-rum/awsRum-Service';
import * as ErrorConstants from 'src/localization/string-ids/error-constants';

const ADM_PRINT_RUM_EVENT = 'ADM_PRINT_VIA_IP';
const CALLBACK_NAME = 'rivPrintCallback';

// 1:1 contract: track whether a print job is in progress
let isPrintInProgress = false;

export interface AdmPrintResult {
    success: boolean;
    message: string;
}

/**
 * Escapes single quotes in the ZPL string to prevent breaking the WLS invocation URL.
 * ZPL from ZPLGenerator typically doesn't contain single quotes (uses ^FD/^FS field markers),
 * but sortation category data could theoretically include them.
 */
function escapeZplForWls(zpl: string): string {
    return zpl.replace(/'/g, "\\'");
}

/**
 * Sends a ZPL label to a Hip printer via ADMPrintApp.
 *
 * @param zpl - ZPL string to print (starts with ^XA, ends with ^XZ)
 * @param printerIP - Static IP of the Hip printer on FC WiFi
 * @param warehouseId - Warehouse ID for metrics
 * @param associate - Associate login for metrics
 * @returns Promise<AdmPrintResult> resolving with success/failure + message
 */
export function admPrint(
    zpl: string,
    printerIP: string,
    warehouseId: string,
    associate: string
): Promise<AdmPrintResult> {
    if (isPrintInProgress) {
        return Promise.resolve({
            success: false,
            message: ErrorConstants.WIRELESS_PRINT_IN_PROGRESS
        });
    }

    isPrintInProgress = true;
    const startTime = Date.now();

    const emitMetric = (status: 'SUCCESS' | 'FAILURE' | 'TIMEOUT') => {
        awsRum.recordEvent(ADM_PRINT_RUM_EVENT, {
            Status: status,
            Latency: Date.now() - startTime,
            Warehouse: warehouseId,
            Associate: associate,
            PrinterIP: printerIP
        });
    };

    return new Promise<AdmPrintResult>((resolve) => {
        let timeoutId: ReturnType<typeof setTimeout>;

        const cleanup = () => {
            isPrintInProgress = false;
            clearTimeout(timeoutId);
            delete (window as any)[CALLBACK_NAME];
        };

        // Register callback on window — this is how ADMPrintApp returns results via Velocity Browser
        (window as any)[CALLBACK_NAME] = (response: AppIntentResponse) => {
            cleanup();

            // Per ADMPrintApp protocol: result !== 0 means success, result === 0 means failure
            if (response.result !== 0) {
                emitMetric('SUCCESS');
                resolve({ success: true, message: printerIP });
                return;
            }

            // Extract failure message from extras
            // ADMPrintApp format: extras[].value = "PRINT_FAILURE : <details>"
            const failureExtra = response.extras?.find(e => e.value?.includes('PRINT_FAILURE'));
            const failureMessage = failureExtra?.value || '';

            emitMetric('FAILURE');
            resolve({
                success: false,
                message: failureMessage || ErrorConstants.WIRELESS_PRINT_FAILED
            });
        };

        // Timeout — if ADMPrintApp doesn't respond within 30s
        timeoutId = setTimeout(() => {
            cleanup();
            emitMetric('TIMEOUT');
            resolve({
                success: false,
                message: ErrorConstants.WIRELESS_PRINT_TIMEOUT
            });
        }, ADM_PRINT_CONFIG.timeoutMs);

        // Invoke ADMPrintApp via WLS protocol
        // Velocity Browser intercepts wls: URLs and dispatches them as Android Intents
        const escapedZpl = escapeZplForWls(zpl);
        window.location.href = `wls:printViaIP('${escapedZpl}','${printerIP}',${ADM_PRINT_CONFIG.port},'${CALLBACK_NAME}')`;
    });
}

/**
 * Returns whether a print job is currently in progress.
 * Useful for UI to disable the print button while waiting for callback.
 */
export function isAdmPrintInProgress(): boolean {
    return isPrintInProgress;
}

/**
 * Resets the print-in-progress state. Use only for testing or error recovery.
 */
export function resetAdmPrintState(): void {
    isPrintInProgress = false;
    delete (window as any)[CALLBACK_NAME];
}
